import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import i18n from '@/i18n';

/**
 * Expected, user-facing refusals — toasted by the hook, kept out of Sentry by
 * the app's MutationCache (it skips `envelope.code`s listed in
 * `meta.expectedErrorCodes`, see App.tsx).
 */
const SKILL_EXISTS = 'skill_exists';
const SKILL_IN_USE = 'skill_in_use';
const SKILL_MUTATION_META = { expectedErrorCodes: [SKILL_EXISTS, SKILL_IN_USE] as readonly string[] };
const codedEnvelope = (error: unknown) => (error as { envelope?: { code?: string } } | null)?.envelope?.code;
import { Skill, CreateSkillData } from '@/types/people';

// Query keys
export const skillKeys = {
  all: ['skills'] as const,
  lists: () => [...skillKeys.all, 'list'] as const,
  list: (organizationId: string) => [...skillKeys.lists(), organizationId] as const,
};

// Fetch skills for an organization
export const useSkills = (organizationId?: string) => {
  return useQuery({
    queryKey: skillKeys.list(organizationId || ''),
    queryFn: async (): Promise<Skill[]> => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('skills')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching skills:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!organizationId,
  });
};

// Fetch skills grouped by category
export const useSkillsByCategory = (organizationId?: string) => {
  return useQuery({
    queryKey: [...skillKeys.list(organizationId || ''), 'by-category'],
    queryFn: async (): Promise<Record<string, Skill[]>> => {
      if (!organizationId) return {};

      const { data, error } = await supabase
        .from('skills')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching skills by category:', error);
        throw error;
      }

      // Group by category
      const grouped = (data || []).reduce((acc, skill) => {
        const category = skill.category || 'Other';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(skill);
        return acc;
      }, {} as Record<string, Skill[]>);

      return grouped;
    },
    enabled: !!organizationId,
  });
};

// Create skill mutation
export const useCreateSkill = () => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: SKILL_MUTATION_META,
    mutationFn: async (data: CreateSkillData & { organization_id: string }) => {
      const { data: skill, error } = await supabase
        .from('skills')
        .insert(data)
        .select()
        .single();

      if (error) {
        // Keep the Postgres code (CreateSkill/DisciplineDialog read 23505 to pick
        // the race winner) and add the expected-refusal envelope.
        if (error.code === '23505' && error.message.includes('unique_skill_per_org')) {
          throw Object.assign(error, { envelope: { code: SKILL_EXISTS } });
        }
        throw error;
      }
      return skill;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
      toast.success(i18n.t('common:people.toast.skill.created', { name: data.name }));
    },
    onError: (error: Error & { code?: string }) => {
      // Handle unique constraint violations
      if (codedEnvelope(error) === SKILL_EXISTS) {
        toast.error(i18n.t('common:people.toast.skill.exists'));
      } else {
        toast.error(i18n.t('common:people.toast.skill.createFailed'), error.message ? { description: error.message } : undefined);
      }
    },
  });
};

// Update skill mutation
export const useUpdateSkill = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<CreateSkillData> & { id: string }) => {
      const { id, ...updateData } = data;

      const { data: skill, error } = await supabase
        .from('skills')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return skill;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
      // person_index carries the names (people grid liveness).
      queryClient.invalidateQueries({ queryKey: ['people'] });
      toast.success(i18n.t('common:people.toast.skill.updated', { name: data.name }));
    },
    onError: (error: Error) => {
      toast.error(i18n.t('common:people.toast.skill.updateFailed'), error.message ? { description: error.message } : undefined);
    },
  });
};

// Delete skill mutation
export const useDeleteSkill = () => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: SKILL_MUTATION_META,
    mutationFn: async (skillId: string) => {
      // First check if skill is in use
      const { data: usageCheck } = await supabase
        .from('person_skills')
        .select('id')
        .eq('skill_id', skillId)
        .limit(1);

      if (usageCheck && usageCheck.length > 0) {
        throw Object.assign(new Error(i18n.t('common:people.toast.skill.inUse')), {
          envelope: { code: SKILL_IN_USE },
        });
      }

      const { error } = await supabase
        .from('skills')
        .delete()
        .eq('id', skillId);

      if (error) throw error;
      return skillId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
      // person_index carries the names (people grid liveness).
      queryClient.invalidateQueries({ queryKey: ['people'] });
      toast.success(i18n.t('common:people.toast.skill.deleted'));
    },
    onError: (error: Error) => {
      toast.error(i18n.t('common:people.toast.skill.deleteFailed'), error.message ? { description: error.message } : undefined);
    },
  });
};

// Get skill usage statistics
export const useSkillUsage = (skillId?: string) => {
  return useQuery({
    queryKey: ['skills', 'usage', skillId],
    queryFn: async () => {
      if (!skillId) return { count: 0, people: [] };

      const { data, error } = await supabase
        .from('person_skills')
        .select(`
          proficiency_level,
          years_experience,
          person:people(id, first_name, last_name, code)
        `)
        .eq('skill_id', skillId);

      if (error) {
        console.error('Error fetching skill usage:', error);
        throw error;
      }

      return {
        count: data?.length || 0,
        people: data?.map(item => ({
          ...item.person,
          proficiency_level: item.proficiency_level,
          years_experience: item.years_experience,
        })).filter(Boolean) || [],
      };
    },
    enabled: !!skillId,
  });
};

// Get popular skills (most used)
export const usePopularSkills = (organizationId?: string, limit = 10) => {
  return useQuery({
    queryKey: ['skills', 'popular', organizationId, limit],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .rpc('get_popular_skills', {
          org_id: organizationId,
          skill_limit: limit
        });

      if (error) {
        console.error('Error fetching popular skills:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!organizationId,
  });
};